import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FileVideoIcon,
    FolderIcon,
    MonitorIcon,
    PencilSimpleIcon,
    PlusIcon,
    TrashIcon,
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import {
    ActionBar,
    AnimatedActionBarModeTransition,
    SelectionBar,
} from '@/components';
import type { ActionConfig } from '@/components/ActionBar';
import {
    ActionType,
    DividerOrientation,
    buildFiltersAction,
    buildSortAction,
    buildMultiSelectToggle,
} from '@/components/ActionBar';
import { SourceType } from '@/types/source';
import { SourceFolder } from '@/types/source-folder';
import { SecurityCameraIconMirrored } from '@/utils/icons';
import { AppRoutes } from '@/constants/routes';
import {
    OverlayNames,
    useFeatureFlags,
    useOverlayManager,
    usePermissions,
} from '@/hooks';
import { Permission } from '@/types/permissions';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { useCameraLimitReached } from '@/features/SettingsView/hooks/useCameraLimitReached';
import { LimitBadge, LimitPopoverContent } from '@/components/LimitBadge';
import type { SortOption, TypeFilter } from '../hooks/useSourcesFilters';
import { SORT_OPTIONS } from '../hooks/useSourcesFilters';
import { useNewSourceOptions } from '../hooks/useNewSourceOptions';

interface SourcesActionBarProps {
    isMultiSelectMode: boolean;
    selectedCount: number;
    totalCount: number;
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    onSearchSubmit?: (value: string) => void;
    searchPlaceholder?: string;
    onExitMultiSelect: () => void;
    onSelectAll: () => void;
    enterMultiSelectMode?: () => void;
    selectionBulkActions?: ActionConfig[];
    typeFilter: TypeFilter;
    onTypeFilterChange: (type: TypeFilter) => void;
    sortOption: SortOption | null;
    onSortOptionChange: (option: SortOption | null) => void;
    selectedFolder?: SourceFolder | null;
    onEditFolder?: (folder: SourceFolder) => void;
    onDeleteFolder?: (folder: SourceFolder) => void;
    onCreateFolder: () => void;
}

export function SourcesActionBar({
    isMultiSelectMode,
    selectedCount,
    totalCount,
    searchValue,
    onSearchChange,
    onSearchSubmit,
    searchPlaceholder,
    onExitMultiSelect,
    onSelectAll,
    enterMultiSelectMode,
    selectionBulkActions = [],
    typeFilter,
    onTypeFilterChange,
    sortOption,
    onSortOptionChange,
    selectedFolder,
    onEditFolder,
    onDeleteFolder,
    onCreateFolder,
}: SourcesActionBarProps) {
    const { t } = useTranslation([I18nNamespace.SOURCES, I18nNamespace.COMMON]);
    const navigate = useNavigate();
    const { open } = useOverlayManager();
    const { canPerform } = usePermissions();
    const { hdmiEnabled, videoFilesEnabled, liveCamerasEnabled } =
        useFeatureFlags();
    const newSourceOptions = useNewSourceOptions();
    const [searchDraft, setSearchDraft] = useState(searchValue || '');
    const isCameraLimitReached = useCameraLimitReached();

    const hasActiveFilters = typeFilter !== 'all';
    const currentSortId = sortOption
        ? `${sortOption.field}-${sortOption.direction}`
        : null;

    const canEditFolder =
        !!selectedFolder &&
        !!onEditFolder &&
        canPerform(Permission.EDIT, selectedFolder.isOwner);
    const canDeleteFolder =
        !!selectedFolder &&
        !!onDeleteFolder &&
        canPerform(Permission.DELETE, selectedFolder.isOwner);

    // Context-specific actions: on "My sources" we expose Manage cameras (a
    // global action); inside a folder we expose its Edit/Delete instead.
    const contextActions: ActionConfig[] = [];
    if (!selectedFolder) {
        if (liveCamerasEnabled) {
            contextActions.push({
                type: ActionType.ITEM,
                icon: SecurityCameraIconMirrored,
                label: t('actions.manageCameras'),
                permission: Permission.EDIT,
                onClick: () => navigate(AppRoutes.CAMERAS),
            });
        }
        if (hdmiEnabled) {
            contextActions.push({
                type: ActionType.ITEM,
                icon: MonitorIcon,
                label: t('actions.setupHdmi'),
                onClick: () => navigate(AppRoutes.SETUP_HDMI),
            });
        }
    } else {
        if (canEditFolder && onEditFolder) {
            contextActions.push({
                type: ActionType.ITEM,
                icon: PencilSimpleIcon,
                label: t('actions.edit'),
                onClick: () => onEditFolder(selectedFolder),
            });
        }
        if (canDeleteFolder && onDeleteFolder) {
            contextActions.push({
                type: ActionType.ITEM,
                icon: TrashIcon,
                label: t('actions.delete'),
                onClick: () => onDeleteFolder(selectedFolder),
            });
        }
    }

    const browseActions: ActionConfig[] = [
        ...(onSearchChange
            ? [
                  {
                      type: ActionType.SEARCH as const,
                      value: searchDraft,
                      onChange: (value: string) => {
                          setSearchDraft(value);
                          onSearchChange(value);
                      },
                      onSubmit: onSearchSubmit,
                      placeholder: searchPlaceholder,
                  },
              ]
            : []),
        { type: ActionType.DIVIDER, orientation: DividerOrientation.VERTICAL },
        buildFiltersAction(t, {
            isActive: hasActiveFilters,
            onClear: () => onTypeFilterChange('all'),
            items: [
                {
                    label: t('filters.allTypes'),
                    isActive: typeFilter === 'all',
                    onClick: () => onTypeFilterChange('all'),
                },
                ...(liveCamerasEnabled || hdmiEnabled
                    ? [
                          {
                              icon: SecurityCameraIconMirrored,
                              label: t('filters.cameras'),
                              isActive: typeFilter === SourceType.CAMERA,
                              onClick: () =>
                                  onTypeFilterChange(SourceType.CAMERA),
                          },
                      ]
                    : []),
                ...(videoFilesEnabled
                    ? [
                          {
                              icon: FileVideoIcon,
                              label: t('filters.files'),
                              isActive: typeFilter === SourceType.FILE,
                              onClick: () =>
                                  onTypeFilterChange(SourceType.FILE),
                          },
                      ]
                    : []),
            ],
        }),
        buildSortAction(
            t,
            SORT_OPTIONS.map((option) => ({
                label: t(option.labelKey),
                isActive: currentSortId === option.id,
                onClick: () =>
                    onSortOptionChange({
                        field: option.field,
                        direction: option.direction,
                    }),
            })),
        ),
        ...(enterMultiSelectMode
            ? [buildMultiSelectToggle(t, enterMultiSelectMode)]
            : []),
        ...contextActions,
        {
            type: ActionType.MENU,
            icon: PlusIcon,
            label: t('actions.newSource'),
            permission: Permission.CREATE,
            items: [
                ...(newSourceOptions.videoFile.enabled
                    ? [
                          {
                              icon: newSourceOptions.videoFile.icon,
                              label: t(newSourceOptions.videoFile.labelKey),
                              onClick: () =>
                                  open(OverlayNames.SOURCE_UPLOAD, {
                                      targetFolderId:
                                          selectedFolder?.id ?? undefined,
                                  }),
                          },
                      ]
                    : []),
                ...(newSourceOptions.liveCamera.enabled
                    ? [
                          {
                              icon: newSourceOptions.liveCamera.icon,
                              label: t(newSourceOptions.liveCamera.labelKey),
                              disabled: isCameraLimitReached,
                              trailingBadge: isCameraLimitReached ? (
                                  <LimitBadge showLabel={true} />
                              ) : undefined,
                              onClick: isCameraLimitReached
                                  ? undefined
                                  : () =>
                                        open(OverlayNames.ADD_CAMERA, {
                                            targetFolderId:
                                                selectedFolder?.id ?? undefined,
                                        }),
                              children: isCameraLimitReached ? (
                                  <LimitPopoverContent
                                      title={t('actions.liveCameraLimitTitle')}
                                      hint={t('actions.liveCameraLimitReached')}
                                  />
                              ) : undefined,
                          },
                      ]
                    : []),
                { separator: true },
                {
                    icon: FolderIcon,
                    label: t('actions.folder'),
                    onClick: onCreateFolder,
                },
            ],
        },
    ];

    return (
        <AnimatedActionBarModeTransition
            isMultiSelectMode={isMultiSelectMode}
            browseSlot={<ActionBar actions={browseActions} />}
            selectionSlot={
                <SelectionBar
                    selectedCount={selectedCount}
                    totalCount={totalCount}
                    onSelectAll={onSelectAll}
                    onExitMultiSelect={onExitMultiSelect}
                    bulkActions={selectionBulkActions}
                    useShortSelectionCount
                />
            }
        />
    );
}
